import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { isOpenMarketPurchase, type InsiderActivityRow } from "@mosaic/shared";
import { api } from "../api/client.js";

/**
 * What the people who run these companies did with their own money.
 *
 * Open-market purchases arrive in the same table as their own noise: grants land on the
 * compensation committee's schedule, so a plain list of acquisitions is mostly calendar.
 *
 * So the panel leads with the counts, so volume cannot be mistaken for activity. It marks
 * a purchase rather than sorting it to the top, because the sequence is the story. It
 * keeps grants visible, because an incentive reset is its own finding. And it computes no
 * composite, which would become the thing read instead of the evidence.
 */

/**
 * What the transaction code means, in the filer's terms, never a judgement such as
 * "bullish".
 *
 * @internal
 */
function actionLabel(t: InsiderActivityRow): string {
  switch (t.code) {
    case "P":
      return t.isDerivative ? "Bought (derivative)" : "Bought on market";
    case "S":
      return t.isDerivative ? "Sold (derivative)" : "Sold on market";
    case "A":
      return t.isDerivative ? "Granted (derivative)" : "Granted";
    case "M":
      return "Exercised";
    case "F":
      return "Withheld for tax";
    case "D":
      return "Disposed to issuer";
    case "G":
      return "Gift";
    default:
      // The code as filed. An unrecognised one is still a transaction that happened,
      // and inventing a phrase for it would state more than the form does.
      return t.rawCode ? `Code ${t.rawCode}` : "Reported";
  }
}

/**
 * The insider's relationship to the issuer, as the form declares it.
 *
 * @internal
 * @param t The transaction, whose reporting owner is described.
 * @returns The officer title (or `Officer`), `Director` and `10% owner` as apply, joined
 *   with ` · `; empty when none is declared.
 */
function role(t: InsiderActivityRow): string {
  const parts: string[] = [];
  if (t.owner.officerTitle) parts.push(t.owner.officerTitle);
  else if (t.owner.isOfficer) parts.push("Officer");
  if (t.owner.isDirector) parts.push("Director");
  if (t.owner.isTenPercentOwner) parts.push("10% owner");
  return parts.join(" · ");
}

/** Grouped thousands, and never a rounded or abbreviated share count. */
function shareCount(raw: string | null): string {
  if (raw === null) return "—";
  const [whole, fraction] = raw.split(".");
  const grouped = whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction ? `${grouped}.${fraction}` : grouped;
}

/**
 * The Section 16 transactions of a situation's insiders, led by counts of open-market
 * purchases, sales and grants. Renders nothing until the activity has loaded.
 */
export function InsiderActivity({ situationId }: { situationId: string }) {
  const activity = useQuery({
    queryKey: ["insiders", situationId],
    queryFn: () => api.situationInsiders(situationId),
  });

  if (!activity.data) return null;
  const { items, openMarketPurchases, openMarketSales, grants, formsRead } = activity.data;

  return (
    <>
      <div className="sechd">Insider activity</div>

      {/*
        No Form 4s read and insiders who did nothing would look identical without this
        sentence: a newly announced separation has no Section 16 filings yet, which is
        not silence from its insiders.
      */}
      {formsRead === 0 ? (
        <p className="ins-none">
          No Section 16 filing has been read for either company yet. Forms 3, 4 and 5 are
          fetched once a company is part of a live situation, so this fills in as they
          are filed — it is not a statement that nobody has traded.
        </p>
      ) : (
        <>
          <p className="ins-sum">
            <strong>{openMarketPurchases}</strong> open-market purchase
            {openMarketPurchases === 1 ? "" : "s"}
            {openMarketSales > 0 && (
              <>
                {" · "}
                <strong>{openMarketSales}</strong> sale{openMarketSales === 1 ? "" : "s"}
              </>
            )}
            {" · "}
            {grants} grant{grants === 1 ? "" : "s"}
            <span className="ins-dim">
              {" "}
              across {formsRead} form{formsRead === 1 ? "" : "s"}
            </span>
          </p>

          {/*
            Its own scroll container: `.dense` sets `nowrap`, so a long officer title
            widens the table past the reading column. Wide content scrolls inside its own
            box; the page body never scrolls sideways.
          */}
          <div className="ins-wrap">
            <table className="dense insiders">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Who</th>
                  <th>What</th>
                  <th className="num">Shares</th>
                  <th className="num">Price</th>
                  <th>Filing</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr
                    // The row's own unique key: two grants on one form can share a date, a security
                    // title and a share count.
                    key={`${t.accession}-${t.isDerivative}-${t.ordinal}`}
                    className={isOpenMarketPurchase(t) ? "ins-buy" : undefined}
                  >
                    <td>{t.transactionDate ?? "—"}</td>
                    <td>
                      <span className="ins-who">{t.owner.name}</span>
                      {role(t) && <span className="ins-dim"> {role(t)}</span>}
                    </td>
                    <td>
                      {actionLabel(t)}
                      {t.side && <span className="ins-dim"> · {t.side}</span>}
                    </td>
                    <td className="num">{shareCount(t.shares)}</td>
                    <td className="num">
                      {t.pricePerShare ? `$${t.pricePerShare.amount}` : "—"}
                      {/*
                        The filing's footnote can qualify its price (a weighted average of
                        several fills, say), and the figure alone would state a precision
                        the filer disclaimed. The mark carries the footnote's own text.
                      */}
                      {t.priceFootnote && (
                        <abbr className="ins-qual" title={t.priceFootnote}>
                          *
                        </abbr>
                      )}
                    </td>
                    <td>
                      <Link to={`/filings/${t.accession}`}>{t.formType}</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
